import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import { useToast } from '../context/ToastContext';
import { Icon } from '../components/SvgIcons';
import './SuperAdminDashboard.css';

const emptyUser = { name: '', email: '', role: 'university_admin', phone: '' };

export default function SuperAdminDashboard() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading]           = useState(true);
  const [tenants, setTenants]           = useState([]);
  const [totalUsers, setTotalUsers]     = useState(0);
  const [finalizedPapers, setFinalizedPapers] = useState(0);
  const [finalizedSplit, setFinalizedSplit]   = useState({ coe: 0, moderator: 0 });

  const [modal, setModal]     = useState(null);
  const [userForm, setUserForm] = useState(emptyUser);
  const [saving, setSaving]   = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tenR, statsR] = await Promise.all([
        API.get('/superadmin/tenants').catch(() => ({ data: { tenants: [] } })),
        API.get('/superadmin/stats').catch(() => ({ data: { total_users: 0 } })),
      ]);
      setTenants(tenR.data.tenants || []);
      setTotalUsers(statsR.data.total_users || 0);
      setFinalizedPapers(statsR.data.finalized_papers_total || 0);
      setFinalizedSplit({
        coe: statsR.data.finalized_papers_coe ?? statsR.data.finalized_papers_university ?? 0,
        moderator: statsR.data.finalized_papers_moderator ?? statsR.data.finalized_papers_college ?? 0,
      });
    } catch {
      showToast('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(userForm).map(([k, v]) => [k, v === '' ? null : v])
      );
      const { data } = await API.post('/users/admin-create', payload);
      showToast(data.message, 'success');
      setModal(null);
      setUserForm(emptyUser);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const recentTenants = [...tenants]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 6);

  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const newThisWeek = tenants.filter(t => new Date(t.created_at) >= weekAgo).length;

  if (loading) return (
    <div className="sad-loading">
      <div className="sad-spinner" />
    </div>
  );

  const statCards = [
    {
      icon: 'Building',
      iconBg: '#dbeafe',
      accentColor: '#3b82f6',
      label: 'Tenants',
      value: tenants.length,
      trend: newThisWeek > 0 ? `+${newThisWeek} added this week` : 'No new tenants this week',
      cta: 'View Tenants',
      onClick: () => navigate('/super-admin/tenants'),
    },
    {
      icon: 'Users',
      iconBg: '#dcfce7',
      accentColor: '#22c55e',
      label: 'Total Users',
      value: totalUsers,
      trend: 'Across all tenants',
      cta: 'Manage Users',
      onClick: () => navigate('/super-admin/user-management'),
    },
    {
      icon: 'FileText',
      iconBg: '#ede9fe',
      accentColor: '#7c3aed',
      label: 'Finalized Papers',
      value: finalizedPapers,
      trend: `${finalizedSplit.coe} by COE · ${finalizedSplit.moderator} by moderators`,
      cta: 'View Finalized Papers',
      onClick: () => navigate('/super-admin/finalized-papers'),
    },
  ];

  const quickActions = [
    {
      icon: 'Building', bg: '#dbeafe',
      label: 'Create Tenant',
      desc: 'Provision a new tenant database',
      action: () => navigate('/super-admin/tenants'),
    },
    {
      icon: 'User', bg: '#dcfce7',
      label: 'Add User',
      desc: 'Create a new platform user',
      action: () => setModal('user'),
    },
    {
      icon: 'Upload', bg: '#f3e8ff',
      label: 'Bulk Upload',
      desc: 'Import users via Excel',
      action: () => navigate('/super-admin/user-management'),
    },
    {
      icon: 'ChartBar', bg: '#fee2e2',
      label: 'Reports',
      desc: 'View platform usage reports',
      action: () => navigate('/super-admin/reports'),
    },
    {
      icon: 'Settings', bg: '#f1f5f9',
      label: 'Settings',
      desc: 'Configure AI and platform settings',
      action: () => navigate('/super-admin/settings'),
    },
  ];

  return (
    <div className="sad-root">

      {/* ── Header ── */}
      <div className="sad-header">
        <div className="sad-header-left">
          <h1 className="sad-greeting">Welcome back, Super Admin</h1>
          <p className="sad-tagline">Here's what's happening with your platform today.</p>
        </div>
        <div className="sad-header-actions">
          <button
            className="sad-hdr-btn"
            title="Settings"
            onClick={() => navigate('/super-admin/settings')}
          >
            <Icon name="Settings" size={18} />
          </button>
        </div>
      </div>

      <div className="sad-body">

        {/* ── Stat Cards ── */}
        <div className="sad-stats-row">
          {statCards.map(card => (
            <div
              key={card.label}
              className={`sad-stat-card${card.alert ? ' sad-stat-alert' : ''}`}
              style={{ '--card-accent': card.accentColor }}
              onClick={card.onClick}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && card.onClick()}
            >
              <div className="sad-stat-top">
                <div className="sad-stat-icon-wrap" style={{ background: card.iconBg }}>
                  <Icon name={card.icon} size={22} color={card.accentColor} />
                </div>
                <span className="sad-stat-cta">{card.cta} →</span>
              </div>
              <div className="sad-stat-value">{card.value}</div>
              <div className="sad-stat-label">{card.label}</div>
              <div className="sad-stat-trend">{card.trend}</div>
            </div>
          ))}
        </div>

        {/* ── Content Grid ── */}
        <div className="sad-grid">

          {/* Quick Actions */}
          <div className="sad-section-card">
            <div className="sad-section-header">
              <h2 className="sad-section-title">Quick Actions</h2>
            </div>
            <div className="sad-actions-grid">
              {quickActions.map(a => (
                <button key={a.label} className="sad-action-tile" onClick={a.action}>
                  <div className="sad-action-icon" style={{ background: a.bg }}><Icon name={a.icon} size={20} color="#475569" /></div>
                  <div className="sad-action-body">
                    <div className="sad-action-name">{a.label}</div>
                    <div className="sad-action-desc">{a.desc}</div>
                  </div>
                  <span className="sad-action-arrow">→</span>
                </button>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="sad-section-card">
            <div className="sad-section-header">
              <h2 className="sad-section-title">Recent Activity</h2>
              <button className="sad-view-all" onClick={() => navigate('/super-admin/tenants')}>
                View All
              </button>
            </div>
            {recentTenants.length === 0 ? (
              <div className="sad-empty">
                <div className="sad-empty-icon"><Icon name="Building" size={28} /></div>
                <p>No tenants provisioned yet</p>
              </div>
            ) : (
              <div className="sad-activity-list">
                {recentTenants.map(t => (
                  <div key={t.id} className="sad-activity-item">
                    <div className="sad-activity-icon"><Icon name="Building" size={18} /></div>
                    <div className="sad-activity-body">
                      <div className="sad-activity-name">{t.university_name}</div>
                      <div className="sad-activity-meta">
                        Tenant provisioned · <code className="sad-slug">{t.slug}</code>
                      </div>
                    </div>
                    <span className="sad-activity-date">
                      {new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Add User Modal ── */}
      {modal === 'user' && (
        <div className="sad-overlay" onClick={() => setModal(null)}>
          <div className="sad-modal" onClick={e => e.stopPropagation()}>
            <div className="sad-modal-header">
              <h3>Add New User</h3>
              <button className="sad-modal-close" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={createUser}>
              <div className="sad-modal-body">
                <div className="sad-form-group">
                  <label className="sad-label">Role *</label>
                  <select
                    className="sad-input"
                    value={userForm.role}
                    onChange={e => setUserForm({ ...userForm, role: e.target.value })}
                  >
                    {[
                      ['university_admin', 'University Admin'],
                      ['college_admin', 'College Admin'],
                      ['hod', 'HOD'],
                      ['coe', 'COE'],
                      ['moderator', 'Moderator'],
                      ['subject_matter_expert', 'Subject Matter Expert'],
                      ['examiner', 'Examiner'],
                    ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                {[
                  ['Full Name *', 'name',  'text',  true,  'Dr. Rahul Sharma'],
                  ['Email *',     'email', 'email', true,  'user@institution.edu'],
                  ['Phone',       'phone', 'tel',   false, '+91 XXXXX XXXXX'],
                ].map(([label, key, type, req, ph]) => (
                  <div key={key} className="sad-form-group">
                    <label className="sad-label">{label}</label>
                    <input
                      type={type}
                      className="sad-input"
                      value={userForm[key] || ''}
                      required={req}
                      placeholder={ph}
                      onChange={e => setUserForm({ ...userForm, [key]: e.target.value })}
                    />
                  </div>
                ))}
                <div className="sad-info-box">
                  Temporary password set to <strong>Test@123</strong>
                </div>
              </div>
              <div className="sad-modal-footer">
                <button type="button" className="sad-btn sad-btn-ghost" onClick={() => setModal(null)}>
                  Cancel
                </button>
                <button type="submit" className="sad-btn sad-btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create & Send Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
